import type {
  CartValidityIssue,
  CartValidityPresentation,
  CartValidityStatus,
  CartValidityViewModel,
} from "@/domain/cart/cart-validity.types";

export type ValidityPresentationInput = Omit<CartValidityViewModel, "presentation">;

function countBySeverity(issues: CartValidityIssue[], severity: CartValidityIssue["severity"]): number {
  return issues.filter((issue) => issue.severity === severity).length;
}

function resolvePrimaryAction(
  status: CartValidityStatus,
  blockingCount: number,
  recoverableCount: number
): CartValidityPresentation["primaryAction"] {
  if (status === "RESTAURANT_UNAVAILABLE" || status === "UNKNOWN") {
    return "refresh_menu";
  }
  if (blockingCount > 0) {
    return "remove_invalid_lines";
  }
  if (recoverableCount > 0) {
    return "review_cart";
  }
  return null;
}

function resolveHeadline(
  status: CartValidityStatus,
  blockingCount: number,
  recoverableCount: number
): string | null {
  if (status === "VALID") return null;
  if (status === "RESTAURANT_UNAVAILABLE") return "Restaurant is not accepting orders";
  if (status === "UNKNOWN") return "Menu is still loading";
  if (blockingCount > 0) {
    return blockingCount === 1 ? "1 item needs attention" : `${blockingCount} items need attention`;
  }
  if (recoverableCount > 0) {
    return recoverableCount === 1
      ? "1 item has updated pricing"
      : `${recoverableCount} items have updated pricing`;
  }
  return null;
}

/** Derives render-oriented validity copy and action hints. No UI components. */
export function deriveValidityPresentation(input: ValidityPresentationInput): CartValidityPresentation {
  const blockingCount = countBySeverity(input.issues, "blocking");
  const recoverableCount = countBySeverity(input.issues, "recoverable");

  return {
    headline: resolveHeadline(input.status, blockingCount, recoverableCount),
    blockingCount,
    recoverableCount,
    primaryAction: resolvePrimaryAction(input.status, blockingCount, recoverableCount),
  };
}
